// Single resolution for a Drip vault's deposit-token logo.
//
// Per-vault icon knowledge belongs to the registry, not to the call sites: a
// new LST should be a new entry in dripVaults.ts and nothing more. Every place
// that shows a vault's token image goes through this hook, so none of them
// branch on a vault id.
//
// Resolution order: the descriptor's bundled `icon`, else the deposit mint's
// remote metadata icon (useTokenIcon). Returns undefined while a remote icon
// is still loading or when nothing resolves, so callers can skip the image
// rather than render a broken one.

import { findVaultById } from "../../constants/dripVaults";
import { useDripVaults } from "../../hooks/useDripVaults";
import { useTokenIcon } from "../../hooks/useTokenIcon";

export const useDripVaultIcon = (
  vaultId: string | undefined,
  /** Fallback mint for callers holding a position/vault state rather than a descriptor. */
  depositMint?: string
): string | undefined => {
  const vaults = useDripVaults();
  const vault = vaultId ? findVaultById(vaults, vaultId) : undefined;
  const mint = vault?.depositMint ?? depositMint;
  // Skip the network call entirely when the registry already bundles an icon.
  const remoteIcon = useTokenIcon(vault?.icon ? undefined : mint);

  return vault?.icon ?? remoteIcon;
};
